import {Component, computed, inject, model, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {SharedModule} from '../../../../core/modules/shared.module';
import {MaterialModule} from '../../../../core/modules/material.module';
import {form, FormField, required, validate} from '@angular/forms/signals';
import {QuillModule} from 'ngx-quill';

interface Banner {
  id?: number;
  text: string;
  dateFrom: string;
  dateTo: string;
  active: boolean;
}

const getInitialBannerData = (): Banner => ({
  text: '',
  dateFrom: '',
  dateTo: '',
  active: false
});

interface News {
  id?: number;
  text: string;
  date: string;
}

const getInitialNewsData = (): News => ({
  text: '',
  date: ''
});

interface SystemText {
  id?: number;
  data: string;
  type: string;
}

const getInitialSystemTextData = (): SystemText => ({
  data: '',
  type: ''
});

@Component({
  selector: 'edit-content-modal',
  templateUrl: './edit-content.modal.html',
  styleUrls: ['./edit-content.modal.scss'],
  imports: [
    SharedModule,
    MaterialModule,
    FormField,
    QuillModule
  ]
})

export class EditContentModal implements OnInit {
  readonly dialogRef = inject(MatDialogRef<EditContentModal>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  disableSave = computed(() => {
    switch (this.type) {
      case 'banner':
        return !this.bannerForm().valid();
      case 'news':
        return !this.newsForm().valid();
      case 'systemText':
        return !this.systemTextForm().valid();
      default:
        return false;
    }
  });
  header = this.data.header;
  type = this.data.type;
  minDate: Date = new Date();
  quillConfig: any = [['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{'list': 'ordered'}, {'list': 'bullet'}],
    [{'link': 'link'}],
    ['clean']];

  bannerModel = signal<Banner>(getInitialBannerData());
  bannerForm = form(this.bannerModel, (banner) => {
    required(banner.text, {message: 'forumTalk.is_required_field'});
  });

  newsModel = signal<News>(getInitialNewsData());
  newsForm = form(this.newsModel, (news) => {
    required(news.text, {message: 'forumTalk.is_required_field'});
    required(news.date, {message: 'forumTalk.is_required_field'});
  });
  newsText;

  systemTextModel = signal<SystemText>(getInitialSystemTextData());
  systemTextForm = form(this.systemTextModel, (systemText) => {
    required(systemText.data, {message: 'forumTalk.is_required_field'});
    required(systemText.type, {message: 'forumTalk.is_required_field'});
    validate(systemText.type, ({value}) => {
      const regex = /^[a-z]+$/;
      if (value() && !regex.test(value())) {
        return {
          kind: 'pattern',
          message: 'forumTalk.system_text_title_description'
        };
      }
      return null;
    });
  });
  systemTextData;

  constructor() {

  }

  ngOnInit(): void {
    if (this.type === 'banner' && this.data.data) {
      this.bannerModel.set({...this.data.data});
    }
    if (this.type === 'news' && this.data.data) {
      this.newsModel.set({...this.data.data});
      this.newsText = this.newsModel().text;
    }
    if (this.type === 'systemText' && this.data.data) {
      this.systemTextModel.set({...this.data.data});
      this.systemTextData = this.systemTextModel().data;
    }
  }

  onSaveClick() {
    if (this.type === 'banner') {
      this.dialogRef.close(this.bannerModel());
    }
    if (this.type === 'news') {
      this.dialogRef.close(this.newsModel());
    }
    if (this.type === 'systemText') {
      this.dialogRef.close(this.systemTextModel());
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  cleanDateField(field) {
    this.bannerModel.update(prev => ({...prev, [field]: null}));
  }

  onDescriptionChange(event: any) {
    if (this.newsModel().text !== event.html) {
      this.newsModel.update(prev => ({
        ...prev,
        text: event.html
      }));
    }
  }

  onSystemTextChange(event: any) {
    if (this.systemTextModel().data !== event.html) {
      this.systemTextModel.update(prev => ({
        ...prev,
        data: event.html
      }));
    }
  }
}
